import type { QueryClient } from "@tanstack/react-query";
import { ClassesApi } from "@/openapi/generated";
import {
  defaultPageSizeForPortal,
  getStudentAvailableClassesQueryKey,
  onlyAvailableOnlineForPortal,
  type StudentAvailableClassesPortal,
} from "@/features/classes/hooks/student-available-classes-query";
import { createAuthenticatedApi } from "@/lib/utils";

const PREFETCH_STALE_TIME_MS = 5 * 60 * 1000;

export async function prefetchStudentAvailableClasses(
  studentId: number,
  queryClient: QueryClient,
  accessToken: string,
  seasonId: number | null | undefined = null,
  locationId: number | null | undefined = null,
  page: number = 1,
  pageSize?: number,
  lastSelectedId?: number | null,
  active?: boolean,
  portal: StudentAvailableClassesPortal = "admin",
) {
  if (!accessToken) {
    throw new Error("Access token is required for prefetching available classes");
  }

  const resolvedPageSize = pageSize ?? defaultPageSizeForPortal(portal);
  const onlyAvailableOnline = onlyAvailableOnlineForPortal(portal);
  const service = createAuthenticatedApi(ClassesApi, accessToken);

  await queryClient.prefetchQuery({
    queryKey: getStudentAvailableClassesQueryKey({
      studentId,
      seasonId,
      locationId,
      page,
      pageSize: resolvedPageSize,
      lastSelectedId: lastSelectedId ?? undefined,
      active,
      portal,
    }),
    queryFn: () =>
      service.apiClassesEnrollmentAvailablePost({
        page,
        pageSize: resolvedPageSize,
        lastSelectedId: lastSelectedId ?? undefined,
        active,
        getAvailableClassesRequest: {
          studentId,
          seasonId: seasonId ?? undefined,
          locationId: locationId ?? undefined,
          onlyAvailableOnline,
        },
      }),
    staleTime: PREFETCH_STALE_TIME_MS,
  });

  return queryClient;
}
